import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Tooltip,
  Chip,
  IconButton,
  Stack,
  Menu,
  MenuItem,
  ListItemText,
  Checkbox,
  ButtonGroup
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import StorageIcon from '@mui/icons-material/Storage';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { useTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import DownloadFileModal from './DownloadFileModal';
import { designTokens } from '../../theme/designTokens';
import { selectQueryOptions, toggleQueryOption } from '../../features/settings/settingsSlice';
import { openAnalysisModal } from '../../features/analysis/analysisSlice';

const TopToolbar = ({
  handleExecuteQuery,
  showEditActions = true,
  compact = false,
  metadataSlot = null,
  isSqlDisabled,
  showSql,
  setShowSql,
  isUndoPossible,
  handleUndo,
  isRedoPossible,
  handleRedo,
  handleReset,
  handleCopySQL,
  metadata,
  filters = [],
  sql = '',
  entities = [],
  fileName = null,
  db = null,
  setSnackbarInfo,
  queryResultData = null,
  loading = false
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const selectedOptions = useSelector(selectQueryOptions);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDropdownOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleOptionToggle = (option) => {
    dispatch(toggleQueryOption(option));
  };

  const handleOpenAnalysis = () => {
    dispatch(openAnalysisModal());
  };

  // Check if we have valid query result data - following same pattern as QueryResults component
  const isAnalyzeEnabled = queryResultData &&
    queryResultData.response &&
    queryResultData.response !== "Retry" &&
    queryResultData.response !== "" &&
    queryResultData.response !== null;

  const controlMinHeight = compact ? 30 : 36;
  const controlPadY = compact ? theme.spacing(0.5) : theme.spacing(1);

  const menuItems = [
    { key: 'patientCount', label: 'Patient Count', disabled: true, checked: true },
    { key: 'queryResults', label: 'Query Results', disabled: true, checked: true },
    { key: 'medicationCount', label: 'Medication Count', disabled: false },
    { key: 'procedureCount', label: 'Procedures Counts', disabled: false },
    { key: 'encounterCount', label: 'Encounters Count', disabled: false },
    { key: 'labCount', label: 'Lab Count', disabled: false },
    { key: 'top10Lists', label: 'Top 10 Lists', disabled: false },
    { key: 'distributionCharts', label: 'Distribution Charts', disabled: false },
    { key: 'distributionMap', label: 'Distribution Map', disabled: false },
  ];

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: designTokens.borderRadius.md / 8,
        p: compact ? theme.spacing(0.5) : theme.spacing(1),
        mb: compact ? theme.spacing(1) : theme.spacing(2),
        boxShadow: theme.shadows[2],
      }}
    >
      <Stack direction="row" spacing={compact ? theme.spacing(1.5) : theme.spacing(2)} alignItems="center">
        {/* Database Section */}
        <Box sx={{ minWidth: 200, display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <StorageIcon
            sx={{
              color: theme.palette.primary.main,
              fontSize: designTokens.fontSize.md
            }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: designTokens.fontWeight.medium }}>
              {metadata?.db || 'No database selected'}
            </Typography>
          </Box>
          {filters?.length > 0 && (
            <Chip
              label={`${filters.length} filter${filters.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                fontSize: designTokens.fontSize.xs,
                height: 18,
                mt: theme.spacing(0.5),
                backgroundColor: theme.palette.info.main + '20',
                color: theme.palette.info.dark,
                fontWeight: designTokens.fontWeight.medium,
              }}
            />
          )}
        </Box>

        {/* Main Actions */}
        <Stack direction="row" spacing={theme.spacing(1)} alignItems="center">
          <ButtonGroup
            variant="contained"
            color="primary"
          >
            <LoadingButton
              size="small"
              onClick={handleExecuteQuery}
              loading={loading}
              loadingPosition="start"
              startIcon={<PlayArrowIcon />}
              sx={{
                fontWeight: designTokens.fontWeight.semibold,
                textTransform: 'none',
                px: theme.spacing(2),
                py: controlPadY,
                fontSize: designTokens.fontSize.sm,
                minHeight: controlMinHeight,
              }}
            >
              Run Query
            </LoadingButton>
            <Button
              size="small"
              onClick={handleDropdownOpen}
              disabled={loading}
              sx={{
                fontWeight: designTokens.fontWeight.semibold,
                textTransform: 'none',
                px: theme.spacing(1),
                py: controlPadY,
                fontSize: designTokens.fontSize.sm,
                minHeight: controlMinHeight,
                minWidth: 'auto',
                borderLeft: `1px solid ${theme.palette.primary.dark}`,
              }}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleDropdownClose}
            PaperProps={{
              sx: {
                borderRadius: designTokens.borderRadius.sm,
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12), 0px 4px 8px rgba(0, 0, 0, 0.08)',
                mt: theme.spacing(0.5),
                minWidth: 200,
                border: `1px solid ${theme.palette.divider}`,
              }
            }}
            MenuListProps={{
              sx: {
                py: theme.spacing(0.5),
              }
            }}
          >
            {menuItems.map((item) => (
              <MenuItem
                key={item.key}
                disabled={item.disabled}
                onClick={item.disabled ? undefined : () => handleOptionToggle(item.key)}
                sx={{ py: theme.spacing(0.5), minHeight: 'auto' }}
              >
                <Checkbox
                  checked={item.disabled ? item.checked : selectedOptions[item.key]}
                  disabled={item.disabled}
                  size="small"
                  sx={{ mr: theme.spacing(1), p: theme.spacing(0.5), pointerEvents: 'none' }}
                />
                <ListItemText
                  primary={item.label}
                  sx={{
                    ...(item.disabled && { color: theme.palette.text.disabled }),
                    '& .MuiListItemText-primary': {
                      fontSize: designTokens.fontSize.sm,
                      fontWeight: designTokens.fontWeight.medium
                    }
                  }}
                />
              </MenuItem>
            ))}
          </Menu>

          {/* <Button
            variant="contained"
            size="small"
            disabled={!isAnalyzeEnabled}
            onClick={handleOpenAnalysis}
            startIcon={<AnalyticsIcon />}
            sx={{
              textTransform: 'none',
              fontSize: designTokens.fontSize.sm,
              px: theme.spacing(2),
              py: theme.spacing(1),
              fontWeight: designTokens.fontWeight.semibold,
              minHeight: 36,
              backgroundColor: isAnalyzeEnabled ? theme.palette.secondary.main : undefined,
              '&:hover': {
                backgroundColor: isAnalyzeEnabled ? theme.palette.secondary.dark : undefined,
              }
            }}
          >
            Analyze
          </Button> */}

          {showEditActions && (
            <>
              <Button
                variant={showSql ? "contained" : "outlined"}
                size="small"
                disabled={isSqlDisabled}
                onClick={() => setShowSql(!showSql)}
                sx={{
                  textTransform: 'none',
                  fontSize: designTokens.fontSize.sm,
                  px: theme.spacing(2),
                  py: theme.spacing(1),
                  fontWeight: designTokens.fontWeight.medium,
                  minHeight: 36,
                }}
              >
                View SQL
              </Button>

              {showSql && sql && (
                <Tooltip title="Copy SQL">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCopySQL}
                    sx={{
                      textTransform: 'none',
                      fontSize: designTokens.fontSize.sm,
                      px: theme.spacing(2),
                      py: theme.spacing(1),
                      borderRadius: designTokens.borderRadius.sm,
                      minHeight: 36,
                      minWidth: 'auto',
                    }}
                  >
                    Copy
                  </Button>
                </Tooltip>
              )}

              {/* Edit Actions */}
              <Stack direction="row" spacing={theme.spacing(1)} sx={{ ml: theme.spacing(2) }}>
                <Button
                  variant="outlined"
                  disabled={!isUndoPossible}
                  onClick={handleUndo}
                  size="small"
                  startIcon={<UndoIcon fontSize="small" />}
                  sx={{
                    textTransform: 'none',
                    fontSize: designTokens.fontSize.sm,
                    px: theme.spacing(1.5),
                    py: theme.spacing(1),
                    color: isUndoPossible ? theme.palette.primary.main : theme.palette.text.disabled,
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                    minHeight: 36,
                    minWidth: 'auto'
                  }}
                >
                  Undo
                </Button>

                <Button
                  variant="outlined"
                  disabled={!isRedoPossible}
                  onClick={handleRedo}
                  size="small"
                  startIcon={<RedoIcon fontSize="small" />}
                  sx={{
                    textTransform: 'none',
                    fontSize: designTokens.fontSize.sm,
                    px: theme.spacing(1.5),
                    py: theme.spacing(1),
                    color: isRedoPossible ? theme.palette.primary.main : theme.palette.text.disabled,
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                    minHeight: 36,
                    minWidth: 'auto'
                  }}
                >
                  Redo
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleReset}
                  size="small"
                  startIcon={<DeleteIcon fontSize="small" />}
                  color='error'
                  sx={{
                    textTransform: 'none',
                    fontSize: designTokens.fontSize.sm,
                    px: theme.spacing(1.5),
                    py: theme.spacing(1),
                    color: theme.palette.error.main,
                    '&:hover': { backgroundColor: theme.palette.error.light + '20' },
                    minHeight: 36,
                    minWidth: 'auto'
                  }}
                >
                  Delete
                </Button>
              </Stack>
            </>
          )}
        </Stack>

        {/* Query Metadata */}
        {metadataSlot && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
            {metadataSlot}
          </Box>
        )}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Download Button */}
        <DownloadFileModal
          entities={entities}
          fileName={fileName}
          sql={sql}
          db={db}
          setSnackbarInfo={setSnackbarInfo}
          loading={loading}
          compact={compact}
        />
      </Stack>
    </Box>
  );
};

export default TopToolbar;